package com.sweetlime.dungeonamigo.presentation.features.character

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.dungeonamigo.cache.Spells
import com.sweetlime.dungeonamigo.compendium.CompendiumRepository
import com.sweetlime.dungeonamigo.model.Character
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SpellPickerSheet(
    character: Character,
    compendiumRepo: CompendiumRepository,
    onAddSpell: (String) -> Unit,
    onRemoveSpell: (String) -> Unit,
    onDismiss: () -> Unit,
) {
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    var query by remember { mutableStateOf("") }
    val results = remember(query) { compendiumRepo.searchSpells(query) }

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState       = sheetState,
        containerColor   = BrandColors.Surface,
        dragHandle = {
            Box(
                Modifier
                    .padding(vertical = 8.dp)
                    .size(32.dp, 4.dp)
                    .clip(CircleShape)
                    .background(BrandColors.SurfaceLight),
            )
        },
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp),
        ) {
            // ── Header ────────────────────────────────────────────────────
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    "Add Spells",
                    color      = BrandColors.TextPrimary,
                    fontSize   = 18.sp,
                    fontWeight = FontWeight.Bold,
                    modifier   = Modifier.weight(1f),
                )
                TextButton(onClick = onDismiss) {
                    Text("Done", color = BrandColors.Parchment, fontWeight = FontWeight.SemiBold)
                }
            }

            Spacer(Modifier.height(8.dp))

            // ── Search ────────────────────────────────────────────────────
            OutlinedTextField(
                value         = query,
                onValueChange = { query = it },
                placeholder   = { Text("Search spells…", color = BrandColors.TextSecondary) },
                leadingIcon   = { Icon(Icons.Filled.Search, null, tint = BrandColors.TextSecondary) },
                singleLine    = true,
                modifier      = Modifier.fillMaxWidth(),
                colors        = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor   = BrandColors.Parchment,
                    unfocusedBorderColor = BrandColors.SurfaceLight,
                    focusedTextColor     = BrandColors.TextPrimary,
                    unfocusedTextColor   = BrandColors.TextPrimary,
                    cursorColor          = BrandColors.Parchment,
                ),
                shape = RoundedCornerShape(10.dp),
            )

            Spacer(Modifier.height(8.dp))

            // ── Results ───────────────────────────────────────────────────
            LazyColumn(
                modifier       = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(2.dp),
            ) {
                if (results.isEmpty()) {
                    item {
                        Box(
                            Modifier.fillMaxWidth().padding(vertical = 32.dp),
                            contentAlignment = Alignment.Center,
                        ) {
                            Text(
                                if (query.isBlank()) "No spells in cache yet" else "No results for \"$query\"",
                                color    = BrandColors.TextSecondary,
                                fontSize = 13.sp,
                            )
                        }
                    }
                } else {
                    items(results, key = { it.slug }) { spell ->
                        val isKnown = spell.slug in character.knownSpells
                        SpellPickerRow(
                            spell   = spell,
                            isKnown = isKnown,
                            onClick = {
                                if (isKnown) onRemoveSpell(spell.slug)
                                else onAddSpell(spell.slug)
                            },
                        )
                    }
                }
                item { Spacer(Modifier.height(32.dp)) }
            }
        }
    }
}

@Composable
private fun SpellPickerRow(spell: Spells, isKnown: Boolean, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(if (isKnown) BrandColors.SurfaceLight else BrandColors.Background)
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment     = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        // Known indicator
        Box(
            modifier         = Modifier.size(20.dp),
            contentAlignment = Alignment.Center,
        ) {
            if (isKnown) {
                Text("✓", color = BrandColors.Parchment, fontSize = 14.sp, fontWeight = FontWeight.Bold)
            }
        }

        // Name
        Text(
            text     = spell.name,
            color    = if (isKnown) BrandColors.Parchment else BrandColors.TextPrimary,
            fontSize = 14.sp,
            fontWeight = if (isKnown) FontWeight.SemiBold else FontWeight.Normal,
            modifier = Modifier.weight(1f),
        )

        // Level
        spell.level?.let { level ->
            Text(
                text  = if (level == "0") "Cantrip" else "Lv $level",
                color = BrandColors.TextSecondary,
                fontSize = 11.sp,
            )
        }

        // School badge
        spell.school?.let { school ->
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(4.dp))
                    .background(BrandColors.SpellBlue.copy(alpha = 0.2f))
                    .padding(horizontal = 6.dp, vertical = 2.dp),
            ) {
                Text(
                    text       = school.replaceFirstChar { it.uppercase() }.take(5),
                    color      = BrandColors.SpellBlue,
                    fontSize   = 10.sp,
                    fontWeight = FontWeight.SemiBold,
                )
            }
        }
    }
}
